// Real Supabase Auth — replaces mockAuth.js
// Uses Supabase Auth REST API directly (no SDK required)
// Sends real password reset emails via Supabase
//
// Configuration comes exclusively from environment variables via platform/config
// (Amendment A12 — no hardcoded URLs/keys, no fallbacks).

import { config } from './platform/config';

const SUPABASE_URL = config.SUPABASE_URL;
const SUPABASE_ANON_KEY = config.SUPABASE_ANON_KEY;
const AUTH_URL = `${SUPABASE_URL}/auth/v1`;
const SESSION_KEY = 'faida_supabase_session';

// ── Capture URL state at module-load time, before anything can modify it ─────
// This runs once when the JS bundle loads, BEFORE React renders.
// Exposes the snapshot so App.js can synchronously check for recovery tokens
// without racing against onAuthStateChanged or the Supabase redirect.
export const initialUrlSnapshot = (() => {
  if (typeof window === 'undefined') return { hash: '', search: '', href: '' };
  const snap = {
    hash: window.location.hash || '',
    search: window.location.search || '',
    href: window.location.href || '',
  };
  // SECURITY: never log the actual URL/hash — recovery links carry access
  // tokens in the hash, and printing them exposes tokens in the console.
  console.log('🌐 [supabaseAuth] URL at module load — hash:', snap.hash ? '(present)' : '(empty)', 'search:', snap.search ? '(present)' : '(empty)');
  return snap;
})();

export const isRecoveryUrl = (() => {
  const h = initialUrlSnapshot.hash;
  const s = initialUrlSnapshot.search;
  const found = (h && h.includes('type=recovery')) || (s && s.includes('type=recovery'));
  if (found) console.log('◇ [supabaseAuth] Recovery URL detected at load.');
  return found;
})();

// ── Helpers ───────────────────────────────────────────────────────────────────

const authHeaders = (token) => ({
  'Content-Type': 'application/json',
  'apikey': SUPABASE_ANON_KEY,
  ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
});

const storeSession = (session) => {
  try { localStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch {}
};

const getStoredSession = () => {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const clearSession = () => localStorage.removeItem(SESSION_KEY);

const isExpired = (session) => {
  if (!session?.expires_at) return true;
  return Date.now() / 1000 > session.expires_at - 60; // 60s buffer
};

const toAppUser = (user, session) => ({
  uid: user.id,
  email: user.email,
  displayName: user.user_metadata?.displayName || user.email?.split('@')[0] || 'User',
  accessToken: session?.access_token,
});

// ── Auth State ────────────────────────────────────────────────────────────────

const listeners = [];
const notifyListeners = (user) => listeners.forEach(cb => cb(user));
let currentSession = null;

// onAuthStateChanged(auth, callback) — returns unsubscribe fn
export const onAuthStateChanged = (_auth, callback) => {
  listeners.push(callback);

  setTimeout(async () => {
    // If we landed on the app via a password-recovery URL, do NOT auto-load
    // a stored session — it would render the main app and hide the reset
    // screen. The recovery flow must run first.
    if (isRecoveryUrl) {
      console.log('◇ [onAuthStateChanged] Recovery URL — skipping stored session load');
      callback(null);
      return;
    }
    const stored = getStoredSession();
    if (!stored) { callback(null); return; }

    if (isExpired(stored) && stored.refresh_token) {
      // Try to refresh the session
      try {
        const res = await fetch(`${AUTH_URL}/token?grant_type=refresh_token`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({ refresh_token: stored.refresh_token }),
        });
        if (res.ok) {
          const newSession = await res.json();
          storeSession(newSession);
          currentSession = newSession;
          callback(toAppUser(newSession.user, newSession));
        } else {
          clearSession();
          callback(null);
        }
      } catch {
        callback(null);
      }
    } else if (!isExpired(stored)) {
      currentSession = stored;
      callback(toAppUser(stored.user, stored));
    } else {
      clearSession();
      callback(null);
    }
  }, 80);

  return () => {
    const idx = listeners.indexOf(callback);
    if (idx > -1) listeners.splice(idx, 1);
  };
};

// ── Sign In ───────────────────────────────────────────────────────────────────

export const signInWithEmailAndPassword = async (_auth, email, password) => {
  const res = await fetch(`${AUTH_URL}/token?grant_type=password`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json();
  if (!res.ok) {
    const msg = (data.error_description || data.message || '').toLowerCase();
    if (msg.includes('invalid') || msg.includes('wrong') || msg.includes('credentials')) {
      throw new Error('auth/wrong-password');
    }
    throw new Error(data.error_description || data.message || 'Login failed');
  }
  storeSession(data);
  currentSession = data;
  const user = toAppUser(data.user, data);
  notifyListeners(user);
  return { user };
};

// ── Sign Up ───────────────────────────────────────────────────────────────────

export const createUserWithEmailAndPassword = async (_auth, email, password) => {
  const res = await fetch(`${AUTH_URL}/signup`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({
      email,
      password,
      data: { displayName: email.split('@')[0] },
    }),
  });
  const data = await res.json();
  if (!res.ok) {
    const msg = (data.error_description || data.message || '').toLowerCase();
    if (msg.includes('already registered') || msg.includes('already been registered') || msg.includes('already exists')) {
      throw new Error('auth/email-already-in-use');
    }
    throw new Error(data.error_description || data.message || 'Signup failed');
  }

  // If Supabase requires email confirmation, session will be null
  if (data.session) {
    storeSession(data.session);
    currentSession = data.session;
    const user = toAppUser(data.user, data.session);
    notifyListeners(user);
    return { user };
  }

  // Email confirmation enabled — user exists but needs to confirm
  const user = toAppUser(data.user, null);
  return { user };
};

// ── Forgot Password (sends real email via Supabase) ───────────────────────────

export const sendPasswordResetEmail = async (_auth, email) => {
  // Supabase /recover expects redirect_to as a QUERY parameter (snake_case),
  // NOT in the JSON body. Body-only redirectTo is silently ignored and
  // Supabase falls back to the dashboard's Site URL.
  //
  // CRITICAL: redirect_to must EXACTLY match an entry in the dashboard's
  // "Redirect URLs" allowlist (Authentication → URL Configuration). A trailing
  // slash mismatch causes Supabase to silently fall back to the Site URL,
  // which sends localhost users to the production site. We strip the trailing
  // slash here because the dashboard lists "http://localhost:3000" without one.
  const redirectTo = window.location.origin; // no trailing slash
  const url = `${AUTH_URL}/recover?redirect_to=${encodeURIComponent(redirectTo)}`;
  console.log('📧 Password reset email requested');
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email }),
  });
  const data = await res.json().catch(() => ({}));
  console.log('📧 Reset email response status:', res.status);
  // Supabase returns 200 even if email not found (security best practice)
  // We always return true — never reveal whether an email is registered
  return true;
};

// ── Email OTP (6-digit code) — used for identity verification without password ──
// Used by Privacy Mode "Forgot PIN" flow. Sends a fresh 6-digit code to the user's
// email so someone with account login but no email access (e.g. a cashier sharing
// the app account) can't reset the PIN.
export const sendOtpToEmail = async (email) => {
  const url = `${AUTH_URL}/otp`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, create_user: false }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data.msg || data.message || data.error_description || 'Failed to send code').trim();
    throw new Error(msg);
  }
  return true;
};

// Verify 6-digit OTP sent to email. Returns truthy on success.
export const verifyEmailOtp = async (email, token) => {
  const url = `${AUTH_URL}/verify`;
  const res = await fetch(url, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ email, token, type: 'email' }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data.msg || data.message || data.error_description || 'Invalid code').trim();
    throw new Error(msg);
  }
  return true;
};

// ── Reset Password (called after clicking link in email) ──────────────────────
// accessToken = the token from the URL hash (#access_token=...&type=recovery)

export const confirmPasswordReset = async (_auth, newPassword, accessToken) => {
  const token = accessToken || currentSession?.access_token;
  if (!token) throw new Error('auth/invalid-action-code');

  const res = await fetch(`${AUTH_URL}/user`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ password: newPassword }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const msg = (data.msg || data.message || data.error_description || '').toLowerCase();
    const code = data.error_code || data.code || '';
    // Map Supabase GoTrue errors to stable codes the UI can translate.
    if (code === 'same_password' || msg.includes('should be different') || msg.includes('different from')) {
      throw new Error('auth/same-password');
    }
    if (code === 'weak_password' || msg.includes('weak') || msg.includes('at least')) {
      throw new Error('auth/weak-password');
    }
    if (code === 'otp_expired' || msg.includes('expired')) {
      throw new Error('auth/expired-action-code');
    }
    if (code === 'invalid_token' || msg.includes('invalid token') || msg.includes('jwt')) {
      throw new Error('auth/invalid-action-code');
    }
    const err = new Error(data.message || data.msg || 'Failed to reset password');
    err.code = code;
    throw err;
  }
  return true;
};

// ── Sign Out ──────────────────────────────────────────────────────────────────

export const signOut = async (_auth) => {
  if (currentSession?.access_token) {
    try {
      await fetch(`${AUTH_URL}/logout`, {
        method: 'POST',
        headers: authHeaders(currentSession.access_token),
      });
    } catch {}
  }
  clearSession();
  currentSession = null;
  notifyListeners(null);
};

// ── Firestore-like helpers (keep localStorage for non-auth app data) ──────────

export const doc = (_db, ...segments) => ({ path: segments.join('/') });

export const setDoc = async (docRef, data) => {
  localStorage.setItem('fs_' + docRef.path, JSON.stringify(data));
};

export const getDoc = async (docRef) => {
  const raw = localStorage.getItem('fs_' + docRef.path);
  return {
    exists: () => !!raw,
    data: () => (raw ? JSON.parse(raw) : null),
  };
};

export const auth = {};
export const db = {};
